// src/common.ts
import { z } from "zod";
var uuidSchema = z.string().uuid();
var isoDateTimeSchema = z.string().datetime({ offset: true });
var isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use YYYY-MM-DD");
var paginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  cursor: z.string().min(1).max(200).optional()
});
function paginated(item) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable()
  });
}
var ERROR_CODES = [
  "bad_request",
  "validation_failed",
  "unauthorized",
  "forbidden",
  "not_found",
  "conflict",
  "idempotency_conflict",
  "rate_limited",
  "provider_error",
  "internal"
];
var errorCodeSchema = z.enum(ERROR_CODES);
var apiErrorSchema = z.object({
  error: z.object({
    code: errorCodeSchema,
    message: z.string(),
    details: z.record(z.unknown()).optional(),
    requestId: z.string().optional()
  })
});

// src/money.ts
import { z as z2 } from "zod";
var CURRENCIES = ["KES"];
var currencySchema = z2.enum(CURRENCIES);
var MINOR_PER_MAJOR = { KES: 100 };
var minorAmountSchema = z2.number().int("Amount must be a whole number of cents").positive("Amount must be greater than zero").max(Number.MAX_SAFE_INTEGER);
var CONTRIBUTION_MIN_MINOR = 1e3;
var CONTRIBUTION_MAX_MINOR = 15e6;
var GOAL_TARGET_MAX_MINOR = 1e10;
function toMinor(input, currency = "KES") {
  const raw = typeof input === "number" ? input.toFixed(2) : input.replace(/[,\s]/g, "");
  const match = /^(\d+)(?:\.(\d{0,2}))?$/.exec(raw);
  if (!match) return null;
  const whole = match[1] ?? "0";
  const fraction = (match[2] ?? "").padEnd(2, "0");
  const minor = Number(whole) * MINOR_PER_MAJOR[currency] + Number(fraction);
  return Number.isSafeInteger(minor) ? minor : null;
}
function formatMoney(minor, currency = "KES") {
  const per = MINOR_PER_MAJOR[currency];
  const negative = minor < 0;
  const abs = Math.abs(minor);
  const whole = Math.floor(abs / per);
  const cents = abs % per;
  const wholeText = whole.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const centsText = cents === 0 ? "" : `.${cents.toString().padStart(2, "0")}`;
  return `${negative ? "-" : ""}${currency} ${wholeText}${centsText}`;
}
function progressPercent(raisedMinor, targetMinor) {
  if (targetMinor <= 0) return 0;
  return Math.max(0, Math.min(100, Math.floor(raisedMinor * 100 / targetMinor)));
}
function feeFromBps(grossMinor, bps) {
  return Math.floor(grossMinor * bps / 1e4);
}

// src/users.ts
import { z as z3 } from "zod";
var displayNameSchema = z3.string().trim().min(1, "Enter a name").max(60);
var userSchema = z3.object({
  id: uuidSchema,
  email: z3.string().email(),
  displayName: z3.string().nullable(),
  isStaff: z3.boolean(),
  createdAt: isoDateTimeSchema
});
var updateMeSchema = z3.object({
  displayName: displayNameSchema
});
var emailSchema = z3.string().trim().toLowerCase().email("Enter a valid email address");
var PASSWORD_MIN_LENGTH = 8;
var passwordSchema = z3.string().min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters`).max(72, "Use 72 characters or fewer");
var signInSchema = z3.object({
  email: emailSchema,
  // Sign in only checks presence; the length rule applies when a password is chosen.
  password: z3.string().min(1, "Enter your password")
});
var signUpSchema = z3.object({ email: emailSchema, password: passwordSchema });
var forgotPasswordSchema = z3.object({ email: emailSchema });
var newPasswordSchema = z3.object({ password: passwordSchema, confirm: z3.string() }).refine((v) => v.password === v.confirm, {
  message: "The passwords do not match",
  path: ["confirm"]
});

// src/groups.ts
import { z as z4 } from "zod";
var GROUP_ROLES = ["owner", "admin", "member"];
var groupRoleSchema = z4.enum(GROUP_ROLES);
var ROLE_RANK = { member: 1, admin: 2, owner: 3 };
function roleAtLeast(role, minimum) {
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}
var groupNameSchema = z4.string().trim().min(1, "Enter a group name").max(80);
var groupDescriptionSchema = z4.string().trim().max(500);
var createGroupSchema = z4.object({
  name: groupNameSchema,
  description: groupDescriptionSchema.optional()
});
var updateGroupSchema = z4.object({
  name: groupNameSchema.optional(),
  description: groupDescriptionSchema.nullable().optional()
}).refine((v) => Object.keys(v).length > 0, "Nothing to update");
var groupSchema = z4.object({
  id: uuidSchema,
  name: z4.string(),
  description: z4.string().nullable(),
  currency: currencySchema,
  myRole: groupRoleSchema,
  memberCount: z4.number().int(),
  archivedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema
});
var memberSchema = z4.object({
  userId: uuidSchema,
  displayName: z4.string().nullable(),
  /** Masked unless the viewer is an admin or owner. */
  email: z4.string(),
  role: groupRoleSchema,
  joinedAt: isoDateTimeSchema
});
var changeRoleSchema = z4.object({
  role: z4.enum(["admin", "member"])
});

// src/invitations.ts
import { z as z5 } from "zod";
var createInvitationSchema = z5.object({
  expiresInDays: z5.number().int().min(1).max(90).optional(),
  maxUses: z5.number().int().min(1).max(1e3).optional()
});
var invitationSchema = z5.object({
  id: uuidSchema,
  groupId: uuidSchema,
  code: z5.string(),
  shareUrl: z5.string().url(),
  expiresAt: isoDateTimeSchema.nullable(),
  maxUses: z5.number().int().nullable(),
  useCount: z5.number().int(),
  revokedAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema
});
var invitationCodeSchema = z5.string().regex(/^[A-Za-z0-9_-]{6,32}$/);
var invitationPreviewSchema = z5.object({
  code: z5.string(),
  groupId: uuidSchema,
  groupName: z5.string(),
  memberCount: z5.number().int(),
  valid: z5.boolean(),
  goal: z5.object({
    title: z5.string(),
    targetAmount: z5.number().int(),
    raisedAmount: z5.number().int(),
    currency: currencySchema
  }).nullable()
});
var acceptInvitationResultSchema = z5.object({
  groupId: uuidSchema,
  alreadyMember: z5.boolean()
});

// src/goals.ts
import { z as z6 } from "zod";
var GOAL_STATUSES = ["active", "completed", "archived"];
var goalStatusSchema = z6.enum(GOAL_STATUSES);
var goalTitleSchema = z6.string().trim().min(1, "Enter a goal title").max(100);
var goalTargetSchema = minorAmountSchema.min(1e4, "Target must be at least KES 100").max(GOAL_TARGET_MAX_MINOR, "Target is too large");
var createGoalSchema = z6.object({
  title: goalTitleSchema,
  description: z6.string().trim().max(1e3).optional(),
  targetAmount: goalTargetSchema,
  deadline: isoDateSchema.optional()
});
var updateGoalSchema = z6.object({
  title: goalTitleSchema.optional(),
  description: z6.string().trim().max(1e3).nullable().optional(),
  targetAmount: goalTargetSchema.optional(),
  deadline: isoDateSchema.nullable().optional(),
  status: goalStatusSchema.optional()
}).refine((v) => Object.keys(v).length > 0, "Nothing to update");
var goalSchema = z6.object({
  id: uuidSchema,
  groupId: uuidSchema,
  title: z6.string(),
  description: z6.string().nullable(),
  targetAmount: z6.number().int(),
  /** Derived from the ledger. */
  raisedAmount: z6.number().int(),
  contributionCount: z6.number().int(),
  currency: currencySchema,
  deadline: isoDateSchema.nullable(),
  status: goalStatusSchema,
  createdAt: isoDateTimeSchema
});

// src/contributions.ts
import { z as z7 } from "zod";
var PAYMENT_STATUSES = [
  "pending",
  "successful",
  "failed",
  "abandoned",
  "reversed"
];
var paymentStatusSchema = z7.enum(PAYMENT_STATUSES);
var TERMINAL_PAYMENT_STATUSES = [
  "successful",
  "failed",
  "reversed"
];
var contributionAmountSchema = minorAmountSchema.min(CONTRIBUTION_MIN_MINOR, "Minimum contribution is KES 10").max(CONTRIBUTION_MAX_MINOR, "Maximum contribution is KES 150,000");
var startContributionSchema = z7.object({
  amount: contributionAmountSchema
});
var IDEMPOTENCY_KEY_HEADER = "idempotency-key";
var idempotencyKeySchema = z7.string().min(8).max(128).regex(/^[A-Za-z0-9_-]+$/);
var paymentAttemptSchema = z7.object({
  id: uuidSchema,
  goalId: uuidSchema,
  groupId: uuidSchema,
  status: paymentStatusSchema,
  grossAmount: z7.number().int(),
  currency: currencySchema,
  reference: z7.string(),
  authorizationUrl: z7.string().url().nullable(),
  failureReason: z7.string().nullable(),
  createdAt: isoDateTimeSchema,
  verifiedAt: isoDateTimeSchema.nullable()
});
var contributionSchema = z7.object({
  id: uuidSchema,
  goalId: uuidSchema,
  contributor: z7.object({ userId: uuidSchema, displayName: z7.string().nullable() }),
  grossAmount: z7.number().int(),
  currency: currencySchema,
  status: z7.enum(["confirmed", "reversed"]),
  confirmedAt: isoDateTimeSchema
});
var activityItemSchema = z7.object({
  id: z7.string(),
  type: z7.enum(["contribution", "member_joined", "goal_created"]),
  actor: z7.object({ userId: uuidSchema, displayName: z7.string().nullable() }).nullable(),
  goalTitle: z7.string().nullable(),
  amount: z7.number().int().nullable(),
  currency: currencySchema.nullable(),
  occurredAt: isoDateTimeSchema
});
function paymentFailureMessage(reason) {
  if (!reason || reason === "initialize_failed") return "No money was taken.";
  if (reason === "amount_mismatch")
    return "The amount paid did not match the request. Our team has been alerted and will follow up.";
  if (reason === "reversed_before_confirmation")
    return "Paystack reversed this payment before it was confirmed.";
  return reason;
}

// src/admin.ts
import { z as z8 } from "zod";
var WEBHOOK_STATUSES = ["received", "processed", "ignored", "failed"];
var webhookStatusSchema = z8.enum(WEBHOOK_STATUSES);
var adminPaymentSearchSchema = z8.object({
  q: z8.string().trim().max(200).optional(),
  status: paymentStatusSchema.optional(),
  groupId: uuidSchema.optional(),
  limit: z8.coerce.number().int().min(1).max(100).default(25),
  cursor: z8.string().max(200).optional()
});
var adminPaymentSchema = z8.object({
  id: uuidSchema,
  reference: z8.string(),
  status: paymentStatusSchema,
  grossAmount: z8.number().int(),
  providerFee: z8.number().int().nullable(),
  chipnFee: z8.number().int(),
  netAmount: z8.number().int().nullable(),
  currency: currencySchema,
  userEmail: z8.string(),
  groupId: uuidSchema,
  groupName: z8.string(),
  goalId: uuidSchema,
  goalTitle: z8.string(),
  channel: z8.string().nullable(),
  failureReason: z8.string().nullable(),
  createdAt: isoDateTimeSchema,
  verifiedAt: isoDateTimeSchema.nullable()
});
var adminWebhookEventSchema = z8.object({
  id: uuidSchema,
  provider: z8.string(),
  eventType: z8.string(),
  providerEventId: z8.string(),
  providerReference: z8.string().nullable(),
  status: webhookStatusSchema,
  attempts: z8.number().int(),
  lastError: z8.string().nullable(),
  receivedAt: isoDateTimeSchema,
  processedAt: isoDateTimeSchema.nullable()
});
var adminWebhookSearchSchema = z8.object({
  status: webhookStatusSchema.optional(),
  eventType: z8.string().max(100).optional(),
  reference: z8.string().max(100).optional(),
  limit: z8.coerce.number().int().min(1).max(100).default(25),
  cursor: z8.string().max(200).optional()
});
var reconciliationQuerySchema = z8.object({
  from: z8.string().date(),
  to: z8.string().date()
});

// src/dates.ts
var MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
var plural = (n, unit) => `${n} ${unit}${n === 1 ? "" : "s"}`;
function timeAgo(iso, now = Date.now()) {
  const seconds = Math.round((now - new Date(iso).getTime()) / 1e3);
  const future = seconds < 0;
  const abs = Math.abs(seconds);
  const words = (n, unit) => future ? `in ${plural(n, unit)}` : `${plural(n, unit)} ago`;
  if (abs < 60) return "just now";
  if (abs < 3600) return words(Math.round(abs / 60), "minute");
  if (abs < 86400) return words(Math.round(abs / 3600), "hour");
  if (abs < 7 * 86400) {
    const days = Math.round(abs / 86400);
    if (days === 1) return future ? "tomorrow" : "yesterday";
    return words(days, "day");
  }
  return formatDate(iso);
}
var dayMonthYear = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
function formatDate(iso) {
  return dayMonthYear(new Date(iso));
}
function deadlineLabel(date, now = /* @__PURE__ */ new Date()) {
  const [y, m, d] = date.split("-").map(Number);
  const due = new Date(y, m - 1, d);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return `${due.getTime() < today.getTime() ? "Ended" : "Due"} ${dayMonthYear(due)}`;
}

// src/forms.ts
import { z as z9 } from "zod";
var goalFormSchema = z9.object({
  title: goalTitleSchema,
  description: z9.string().trim().max(1e3).optional(),
  target: z9.string().trim().min(1, "Enter a target amount"),
  /** YYYY-MM-DD or empty. */
  deadline: z9.string().optional()
}).superRefine((v, ctx) => {
  const minor = toMinor(v.target);
  const parsed = createGoalSchema.shape.targetAmount.safeParse(minor ?? Number.NaN);
  if (minor === null)
    ctx.addIssue({
      code: "custom",
      path: ["target"],
      message: "Enter an amount like 60000 or 60,000"
    });
  else if (!parsed.success)
    ctx.addIssue({
      code: "custom",
      path: ["target"],
      message: parsed.error.issues[0]?.message ?? "Invalid amount"
    });
  if (v.deadline && !createGoalSchema.shape.deadline.safeParse(v.deadline).success) {
    ctx.addIssue({ code: "custom", path: ["deadline"], message: "Use the format YYYY-MM-DD" });
  } else if (v.deadline && v.deadline < (/* @__PURE__ */ new Date()).toISOString().slice(0, 10)) {
    ctx.addIssue({ code: "custom", path: ["deadline"], message: "Pick a date in the future" });
  }
});
function toCreateGoalInput(v) {
  return createGoalSchema.parse({
    title: v.title,
    description: v.description || void 0,
    targetAmount: toMinor(v.target),
    deadline: v.deadline || void 0
  });
}
var contributionFormSchema = z9.object({
  amount: z9.string().trim().refine((v) => toMinor(v) !== null, "Enter an amount like 1000 or 1,000").refine(
    (v) => (toMinor(v) ?? 0) >= CONTRIBUTION_MIN_MINOR,
    `Minimum is ${formatMoney(CONTRIBUTION_MIN_MINOR)}`
  ).refine(
    (v) => (toMinor(v) ?? 0) <= CONTRIBUTION_MAX_MINOR,
    `Maximum is ${formatMoney(CONTRIBUTION_MAX_MINOR)}`
  )
});
var QUICK_CONTRIBUTION_AMOUNTS = [5e4, 1e5, 25e4, 5e5];
export {
  CONTRIBUTION_MAX_MINOR,
  CONTRIBUTION_MIN_MINOR,
  CURRENCIES,
  ERROR_CODES,
  GOAL_STATUSES,
  GOAL_TARGET_MAX_MINOR,
  GROUP_ROLES,
  IDEMPOTENCY_KEY_HEADER,
  PASSWORD_MIN_LENGTH,
  PAYMENT_STATUSES,
  QUICK_CONTRIBUTION_AMOUNTS,
  TERMINAL_PAYMENT_STATUSES,
  WEBHOOK_STATUSES,
  acceptInvitationResultSchema,
  activityItemSchema,
  adminPaymentSchema,
  adminPaymentSearchSchema,
  adminWebhookEventSchema,
  adminWebhookSearchSchema,
  apiErrorSchema,
  changeRoleSchema,
  contributionAmountSchema,
  contributionFormSchema,
  contributionSchema,
  createGoalSchema,
  createGroupSchema,
  createInvitationSchema,
  currencySchema,
  deadlineLabel,
  displayNameSchema,
  errorCodeSchema,
  feeFromBps,
  forgotPasswordSchema,
  formatDate,
  formatMoney,
  goalFormSchema,
  goalSchema,
  goalStatusSchema,
  goalTargetSchema,
  goalTitleSchema,
  groupDescriptionSchema,
  groupNameSchema,
  groupRoleSchema,
  groupSchema,
  idempotencyKeySchema,
  invitationCodeSchema,
  invitationPreviewSchema,
  invitationSchema,
  isoDateSchema,
  isoDateTimeSchema,
  memberSchema,
  minorAmountSchema,
  newPasswordSchema,
  paginated,
  paginationQuerySchema,
  passwordSchema,
  paymentAttemptSchema,
  paymentFailureMessage,
  paymentStatusSchema,
  progressPercent,
  reconciliationQuerySchema,
  roleAtLeast,
  signInSchema,
  signUpSchema,
  startContributionSchema,
  timeAgo,
  toCreateGoalInput,
  toMinor,
  updateGoalSchema,
  updateGroupSchema,
  updateMeSchema,
  userSchema,
  uuidSchema,
  webhookStatusSchema
};
